import type { AccountAuthPort, AuthCapabilities } from "./ports.ts";
import type { CloudAuthChallengePurpose } from "@orni/sdk-typescript";

type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

const environment = (import.meta as ImportMeta & {
  env?: Record<string, string | undefined>;
}).env ?? {};

const clientHeaders = {
  "X-Orni-Client": "web",
  "X-Orni-Client-Version": environment.VITE_ORNI_APP_VERSION?.trim() || "0.1.0",
  "X-Orni-Protocol-Version": "1",
};

export class BrowserAccountAuth implements AccountAuthPort {
  constructor(private readonly fetcher: Fetcher = (input, init) => globalThis.fetch(input, init)) {}

  async capabilities(): Promise<AuthCapabilities> {
    const payload = await this.json("/v1/auth/config", { method: "GET" });
    return {
      emailCode: requiredBoolean(payload.emailCode, "emailCode"),
      password: requiredBoolean(payload.password, "password"),
      google: requiredBoolean(payload.google, "google"),
      github: requiredBoolean(payload.github, "github"),
    };
  }

  async accountInfo(): Promise<{ email: string; passwordConfigured: boolean }> {
    const payload = await this.json("/auth/account", { method: "GET" });
    return {
      email: requiredString(payload.email, "email"),
      passwordConfigured: requiredBoolean(payload.passwordConfigured, "passwordConfigured"),
    };
  }

  async requestEmailCode(email: string, purpose: CloudAuthChallengePurpose = "sign_in"): Promise<{ challengeId: string; expiresAt: string }> {
    const payload = await this.json("/v1/auth/email/challenges", jsonRequest({ email, purpose }));
    return {
      challengeId: requiredString(payload.challengeId, "challengeId"),
      expiresAt: requiredString(payload.expiresAt, "expiresAt"),
    };
  }

  async verifyEmailCode(input: { challengeId: string; email: string; code: string }): Promise<void> {
    await this.empty("/auth/email/verify", jsonRequest(input));
  }

  async passwordLogin(input: { email: string; password: string }): Promise<void> {
    await this.empty("/auth/password/login", jsonRequest(input));
  }

  async setPassword(input: { currentPassword?: string; password: string }): Promise<void> {
    await this.empty("/auth/password", jsonRequest(input, "PUT"));
  }

  async verifyPasswordReset(input: { challengeId: string; email: string; code: string }): Promise<void> {
    await this.empty("/auth/password/reset/verify", jsonRequest(input));
  }

  async completePasswordReset(input: { password: string }): Promise<void> {
    await this.empty("/auth/password/reset", jsonRequest(input));
  }

  async createNativeCallback(input: { state: string; redirectUri: string }): Promise<string> {
    const payload = await this.json("/auth/native/codes", jsonRequest(input));
    return requiredString(payload.callbackUrl, "callbackUrl");
  }

  externalLoginUrl(
    provider: "google" | "github",
    input: { returnTo: string; nativeState?: string; nativeRedirectUri?: string },
  ): string {
    const query = new URLSearchParams({ return_to: input.returnTo });
    if (input.nativeState && input.nativeRedirectUri) {
      query.set("native", "1");
      query.set("state", input.nativeState);
      query.set("redirect_uri", input.nativeRedirectUri);
    }
    return `/auth/external/${provider}/start?${query.toString()}`;
  }

  private async empty(input: string, init: RequestInit): Promise<void> {
    const response = await this.fetcher(input, {
      ...init,
      credentials: "include",
      headers: { ...clientHeaders, ...init.headers },
    });
    if (!response.ok) throw await accountError(response);
  }

  private async json(input: string, init: RequestInit): Promise<Record<string, unknown>> {
    const response = await this.fetcher(input, {
      ...init,
      credentials: "include",
      headers: { ...clientHeaders, ...init.headers },
    });
    if (!response.ok) throw await accountError(response);
    const payload: unknown = await response.json();
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      throw new Error("Orni returned an invalid authentication response.");
    }
    return payload as Record<string, unknown>;
  }
}

export const unavailableAccountAuth: AccountAuthPort = {
  async capabilities() {
    return { emailCode: false, password: false, google: false, github: false };
  },
  async accountInfo() { throw new Error("Account login opens in the system browser."); },
  async requestEmailCode() { throw new Error("Account login opens in the system browser."); },
  async verifyEmailCode() { throw new Error("Account login opens in the system browser."); },
  async passwordLogin() { throw new Error("Account login opens in the system browser."); },
  async setPassword() { throw new Error("Account login opens in the system browser."); },
  async verifyPasswordReset() { throw new Error("Account login opens in the system browser."); },
  async completePasswordReset() { throw new Error("Account login opens in the system browser."); },
  async createNativeCallback() { throw new Error("Account login opens in the system browser."); },
  externalLoginUrl() { return "/login"; },
};

function jsonRequest(body: unknown, method = "POST"): RequestInit {
  return {
    method,
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

async function accountError(response: Response): Promise<Error> {
  try {
    const payload = await response.json() as { message?: unknown };
    if (typeof payload.message === "string" && payload.message.trim()) return new Error(payload.message);
  } catch {
    // The status remains useful when a proxy returns a non-JSON error page.
  }
  return new Error(`Authentication failed with HTTP ${response.status}.`);
}

function requiredString(value: unknown, field: string): string {
  if (typeof value !== "string" || !value.trim()) throw new Error(`Authentication response omitted ${field}.`);
  return value;
}

function requiredBoolean(value: unknown, field: string): boolean {
  if (typeof value !== "boolean") throw new Error(`Authentication response omitted ${field}.`);
  return value;
}
